package org.lennycodes.hldi.pages
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.style.breakpoint.ResponsiveValues
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.lennycodes.hldi.components.CustomSection
import org.lennycodes.hldi.components.MissionVisionCard
import org.lennycodes.hldi.components.missionBlocks
import org.lennycodes.hldi.constants.HLDITheme
import org.lennycodes.hldi.data.Milestone
import org.lennycodes.hldi.layouts.MainLayout
import org.lennycodes.hldi.pages.admin.AdminRepository
import org.lennycodes.hldi.styles.AboutHeroSectionStyle
import org.lennycodes.hldi.styles.AboutHeroTextStyle
import org.lennycodes.hldi.styles.AboutHeroTitleStyle
import org.lennycodes.hldi.styles.FooterWaveSvg
import org.lennycodes.hldi.styles.SectionWaveSvg
import org.lennycodes.hldi.styles.SpinnerStyle
import org.lennycodes.hldi.styles.TimelineAccentBar
import org.lennycodes.hldi.styles.TimelineContainer
import org.lennycodes.hldi.styles.TimelineDivider
import org.lennycodes.hldi.styles.TimelineEventText
import org.lennycodes.hldi.styles.TimelineRow
import org.lennycodes.hldi.styles.TimelineYearBadge
import org.lennycodes.hldi.styles.coreValuesStyle

@Composable
@Page("/about")
fun AboutPage() {
    var isLoading by remember { mutableStateOf(false) }
    var milestones by remember { mutableStateOf<List<Milestone>>(emptyList()) }
    LaunchedEffect(Unit){
        milestones = AdminRepository.getAllMilestones()
    }
    MainLayout {
        // Hero Section
        Section(
            attrs = Modifier.padding(50.px, 20.px).then( AboutHeroSectionStyle.toModifier())
                .toAttrs()
        ) {
            H1(attrs = AboutHeroTitleStyle.toAttrs()) {
                Text("About HLDI")
            }
            P(attrs = AboutHeroTextStyle.toAttrs()) {
                Text("Learn about our mission, vision, motto and the values that drive our humanitarian work.")
            }

            SectionWaveSvg() // wave sits at the bottom edge, dipping into the next section
        }


// Mission & Vision
        Section(
            attrs = Modifier.margin(32.px)
                .toAttrs()
        ) {
            SimpleGrid(
                numColumns = ResponsiveValues(
                    1, // base (xs)
                    1, // sm
                    2, // md
                    3, // lg+ — capped at 3 since there are only 3 cards
                    3,
                    3
                ),
                modifier = Modifier.gap(48.px)
            ) {
                missionBlocks.forEach { block ->
                    MissionVisionCard(block)
                }
            }
        }


// Core Values
        CustomSection(
            title = "Core Values",
            subtitle = "In addition to the diocesan values the department specifically believes in the below principles that guide our work and define our approach to humanitarian service."
        ) {
            SimpleGrid(
                numColumns = ResponsiveValues(
                    1, // base (xs)
                    1, // sm
                    2, // md
                    3,  // lg+
                    3,
                    4
                ),
                modifier = Modifier.gap(24.px)
            ) {
                listOf(
                    "❤️ Compassion" to "We act with empathy and genuine care for those we serve, treating every individual with dignity and respect.",
                    "⚡ Efficiency" to "We use our time, skills, and resources wisely, maximizing impact while minimizing waste in everything we do.",
                    "🙏 Humility" to "We approach our work with humility, listening to and learning from the communities we serve rather than dictating to them.",
                    "🤝 Teamwork" to "We achieve more together, fostering collaboration among our staff, partners, and the communities we serve.",
                    "✨ Spirituality" to "We draw strength and guidance from shared faith and values, honoring the spiritual diversity of those we serve.",
                    "🏆 Commitment to Excellence" to "We hold ourselves to the highest standards, continuously striving to improve the quality and impact of our programs."
                ).forEach { (title, description) ->
                    Div(
                        attrs = Modifier
                            .padding(32.px)
                            .textAlign(TextAlign.Center)
                            .then(coreValuesStyle.toModifier())
                            .toAttrs()

                    ) {
                        H3(
                            attrs = {
                                style {
                                    fontSize(18.px)
                                    marginBottom(12.px)
                                    color(HLDITheme.Colors.primary)
                                }
                            }
                        ) {
                            Text(title)
                        }
                        P(
                            attrs = {
                                style {
                                    fontSize(14.px)
                                    lineHeight("1.6")
                                    color(HLDITheme.Colors.mediumGray)
                                }
                            }
                        ) {
                            Text(description)
                        }
                    }
                }
            }
        }

        // Timeline
        CustomSection(
            title = "Our Journey",
            subtitle = "Key milestones in our mission to protect and empower communities.",
            modifier = Modifier.backgroundColor(HLDITheme.Colors.white)
        ) {
            Div(attrs = TimelineContainer.toAttrs()) {

                Div(attrs = TimelineContainer.toAttrs()) {

                    when {

                        isLoading -> {

                            Div(
                                attrs = Modifier
                                    .fillMaxWidth()
                                    .padding(40.px)
                                    .toAttrs {
                                        style {
                                            property("display", "flex")
                                            property("justify-content", "center")
                                        }
                                    }
                            ) {
                                // Simple circular loader
                                Div(
                                    attrs = Modifier
                                        .size(40.px)
                                        .then(SpinnerStyle.toModifier())
                                        .toAttrs ()
                                )
                            }

                        }

                        milestones.isEmpty() -> {

                            P(
                                attrs = TimelineEventText.toAttrs()
                            ) {
                                Text("...")
                            }

                        }

                        else -> {

                            milestones.forEachIndexed { index, milestone ->

                                Div(attrs = TimelineRow.toAttrs()) {

                                    Div(attrs = TimelineAccentBar.toAttrs())

                                    Div(attrs = TimelineYearBadge.toAttrs()) {
                                        Text(milestone.year)
                                    }

                                    P(attrs = TimelineEventText.toAttrs()) {
                                        Text(milestone.description)
                                    }
                                }

                                if (index != milestones.lastIndex) {
                                    Hr(attrs = TimelineDivider.toAttrs())
                                }
                            }

                        }
                    }
                }
            }
        }
    }
}